import React, { useCallback } from 'react';
import { DateTime } from 'luxon';

import { SearchableDropdownField } from 'ui-components/components/dropdown';
import { FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { messages } from './messages';
import styles from './styles.module.less';
import { BLOCKING_BEHAVIOR } from '../../../../../../constants/fraud-constants';
import { LossPreventionCard } from '../../loss-prevention-card';

const HOURS_IN_DAY = 24;
const FIELD_START_HOUR = 'startHour';
const FIELD_END_HOUR = 'endHour';

type UnusualOrderTimeCardProps = {
  isCardDisabled: boolean;
  startHour: number;
  endHour: number;
  onFeatureToggle: (enabled: boolean) => void;
  onSettingChange: (field: typeof FIELD_START_HOUR | typeof FIELD_END_HOUR, value: number) => void;
};

// Generate hour options: 12am, 1am, 2am, ... 11pm
// Static formatting only - no timezone logic needed
const HOUR_OPTIONS = Array.from({ length: HOURS_IN_DAY }, (_, i) => ({
  value: String(i),
  label: DateTime.fromObject({ hour: i }).toFormat('ha').toLowerCase(),
}));

export const UnusualOrderTimeCard = ({
  isCardDisabled,
  startHour,
  endHour,
  onFeatureToggle,
  onSettingChange,
}: UnusualOrderTimeCardProps) => {
  const createHourChangeHandler = useCallback(
    (field: typeof FIELD_START_HOUR | typeof FIELD_END_HOUR) => (selectedOption: string | null) => {
      if (!selectedOption) return;
      const value = parseInt(selectedOption, 10);
      onSettingChange(field, value);
    },
    [onSettingChange],
  );

  const handleStartHourChange = createHourChangeHandler(FIELD_START_HOUR);
  const handleEndHourChange = createHourChangeHandler(FIELD_END_HOUR);

  const settings = (
    <FlexRow gap={Gap.Space12}>
      <Text textType={TextType.Label} value={messages.flagOrdersBetween} />
      <SearchableDropdownField
        className={styles['time-dropdown']}
        id='start-hour-dropdown'
        isDisabled={isCardDisabled}
        onItemSelected={handleStartHourChange}
        options={HOUR_OPTIONS}
        selectedItemValue={String(startHour)}
      />
      <Text textType={TextType.Label} value={messages.and} />
      <SearchableDropdownField
        className={styles['time-dropdown']}
        id='end-hour-dropdown'
        isDisabled={isCardDisabled}
        onItemSelected={handleEndHourChange}
        options={HOUR_OPTIONS}
        selectedItemValue={String(endHour)}
      />
    </FlexRow>
  );

  return (
    <LossPreventionCard
      blockingBehavior={BLOCKING_BEHAVIOR.INFO}
      description={messages.description}
      isCardDisabled={isCardDisabled}
      onToggle={onFeatureToggle}
      title={messages.title}
    >
      {settings}
    </LossPreventionCard>
  );
};
